import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatMoney, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";
import { TradeCard } from "./day-entry/TradeCard";
import { ReflectionSection } from "./day-entry/ReflectionSection";
import { AICoachSection } from "./day-entry/AICoachSection";

const EMPTY_TRADE = { pair: "", direction: "long", lot_size: 0, entry: 0, exit: 0, pnl: 0, notes: "" };
const DRAFT_KEY = (date) => `journal-draft:${date}`;
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const buildPayload = (date, entry) => ({
  date,
  ...entry,
  trades: entry.trades.map((t) => ({
    pair: t.pair || "?",
    direction: t.direction || "long",
    lot_size: parseFloat(t.lot_size) || 0,
    entry: parseFloat(t.entry) || 0,
    exit: parseFloat(t.exit) || 0,
    pnl: parseFloat(t.pnl) || 0,
    outcome: t.outcome || null,
    summary: t.summary || "",
    notes: t.notes || "",
    region_flag: t.region_flag || null,
    account_id: t.account_id || null,
    session: t.session || null,
    session_time: t.session_time || null,
    strategy: t.strategy || null,
  })),
});

export default function DayEntryModal({ date, onClose, onSaved }) {
  const { user } = useAuth();
  const [entry, setEntry] = useState({
    setup_strategy: "",
    entry_exit_reasoning: "",
    emotions: "",
    mistakes: "",
    lessons: "",
    trades: [],
    tags: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiText, setAiText] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const initialLoadDone = useRef(false);
  const draftTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/accounts");
        setAccounts((data || []).filter((a) => a.status !== "archived"));
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      let restored = false;
      try {
        // Attempt to restore a fresh local draft first
        try {
          const raw = localStorage.getItem(DRAFT_KEY(date));
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && parsed.savedAt && Date.now() - parsed.savedAt < DRAFT_TTL_MS) {
              setEntry(parsed.entry);
              setDraftSavedAt(parsed.savedAt);
              setDraftRestored(true);
              restored = true;
            } else {
              localStorage.removeItem(DRAFT_KEY(date));
            }
          }
        } catch (_e) { /* corrupt draft — ignore */ }

        const { data } = await api.get(`/journal/entries/${date}`);
        if (data && !restored) {
          setEntry({
            setup_strategy: data.setup_strategy || "",
            entry_exit_reasoning: data.entry_exit_reasoning || "",
            emotions: data.emotions || "",
            mistakes: data.mistakes || "",
            lessons: data.lessons || "",
            trades: data.trades || [],
            tags: data.tags || [],
          });
        }
        if (data?.ai_review?.text) setAiText(data.ai_review.text);
      } finally {
        setLoading(false);
        // Allow the debounced draft-saver to kick in only after the initial state is settled
        setTimeout(() => { initialLoadDone.current = true; }, 50);
      }
    })();
  }, [date]);

  // Debounced draft autosave
  useEffect(() => {
    if (!initialLoadDone.current) return;
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => {
      try {
        const savedAt = Date.now();
        localStorage.setItem(DRAFT_KEY(date), JSON.stringify({ savedAt, entry }));
        setDraftSavedAt(savedAt);
      } catch (_e) { /* quota exceeded — silently ignore */ }
    }, 600);
    return () => clearTimeout(draftTimer.current);
  }, [entry, date]);

  const clearDraft = useCallback(() => {
    try { localStorage.removeItem(DRAFT_KEY(date)); } catch (_e) { /* noop */ }
    setDraftSavedAt(null);
    setDraftRestored(false);
  }, [date]);

  const totalPnl = entry.trades.reduce((s, t) => s + (parseFloat(t.pnl) || 0), 0);

  const updateField = useCallback((field, val) => setEntry((e) => ({ ...e, [field]: val })), []);
  const updateTrade = useCallback((i, field, val) =>
    setEntry((e) => ({
      ...e,
      trades: e.trades.map((t, idx) => (idx === i ? { ...t, [field]: val } : t)),
    })), []);
  const addTrade = useCallback(() =>
    setEntry((e) => ({ ...e, trades: [...e.trades, { ...EMPTY_TRADE }] })), []);
  const removeTrade = useCallback((i) =>
    setEntry((e) => ({ ...e, trades: e.trades.filter((_, idx) => idx !== i) })), []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/journal/entries", buildPayload(date, entry));
      clearDraft();
      onSaved?.();
      onClose?.();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!confirm("Delete this day's entry?")) return;
    await api.delete(`/journal/entries/${date}`);
    clearDraft();
    onSaved?.();
    onClose?.();
  };

  const requestAi = async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      await api.post("/journal/entries", buildPayload(date, entry));
      clearDraft();
      onSaved?.();
      const { data } = await api.post("/journal/ai-review", { date });
      setAiText(data.text);
    } catch (e) {
      setAiError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAiLoading(false);
    }
  };

  const shareDay = async () => {
    try {
      const { data } = await api.post(`/journal/entries/${date}/share`);
      const url = `${window.location.origin}/share/${data.token}`;
      try { await navigator.clipboard.writeText(url); } catch (_e) { console.warn("clipboard blocked", _e); }
      alert(`Share link copied:\n${url}`);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || "Save the entry first.");
    }
  };

  const dateObj = new Date(`${date}T12:00:00`);
  const dateLabel = dateObj.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 overflow-y-auto bg-[#0F0E0C]/80 backdrop-blur-sm fade-in"
      onClick={onClose}
    >
      <div
        data-testid={TID.dayModal}
        className="relative w-full max-w-3xl bg-[#1A1815] border border-[#2E2A25] rounded-sm slide-up my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#1A1815]/95 backdrop-blur border-b border-[#2E2A25] px-6 py-4 flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CFA66A]">
              &mdash; Journal entry
            </p>
            <h2 className="font-head text-2xl font-medium text-[#EBE5D9] mt-1">
              {dateLabel}
            </h2>
            {draftRestored && (
              <p data-testid="draft-restored-notice" className="font-mono text-[10px] uppercase tracking-widest text-[#CFA66A] mt-1">
                Unsaved draft restored
              </p>
            )}
            {!draftRestored && draftSavedAt && (
              <p data-testid="draft-saved-indicator" className="font-mono text-[10px] uppercase tracking-widest text-[#7A9E80]/70 mt-1">
                Draft saved · {new Date(draftSavedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E]">
                Day P&amp;L
              </p>
              <p
                data-testid={TID.dayTotalPnl}
                className={`font-mono text-xl font-medium ${
                  totalPnl > 0 ? "text-[#7A9E80]" : totalPnl < 0 ? "text-[#B56258]" : "text-[#EBE5D9]"
                }`}
              >
                {formatMoney(totalPnl, user?.currency)}
              </p>
            </div>
            <button
              data-testid={TID.closeModal}
              onClick={onClose}
              className="text-[#A39A8E] hover:text-[#EBE5D9] transition-colors w-8 h-8 flex items-center justify-center border border-[#2E2A25] hover:border-[#CFA66A] rounded-sm"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center font-mono text-sm text-[#A39A8E]">Loading...</div>
        ) : (
          <div className="p-6 space-y-8">
            <section>
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="font-head text-lg text-[#EBE5D9]">Trades</h3>
                <button
                  data-testid={TID.addTrade}
                  onClick={addTrade}
                  className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#CFA66A] hover:text-[#EBE5D9] transition-colors"
                >
                  + Add trade
                </button>
              </div>

              {entry.trades.length === 0 && (
                <p className="text-sm text-[#A39A8E] font-mono border border-dashed border-[#2E2A25] p-4 text-center rounded-sm">
                  No trades logged. Click &quot;Add trade&quot; to start.
                </p>
              )}

              <div className="space-y-3">
                {entry.trades.map((t, i) => (
                  <TradeCard key={i} trade={t} index={i} onUpdate={updateTrade} onRemove={removeTrade} accounts={accounts} />
                ))}
              </div>
            </section>

            <ReflectionSection entry={entry} onFieldChange={updateField} />

            <AICoachSection
              aiText={aiText}
              aiLoading={aiLoading}
              aiError={aiError}
              onRequestAi={requestAi}
            />
          </div>
        )}

        {/* Footer */}
        <div className="sticky bottom-0 bg-[#1A1815]/95 backdrop-blur border-t border-[#2E2A25] px-6 py-4 flex items-center justify-between gap-3">
          <button
            data-testid={TID.deleteEntry}
            onClick={del}
            className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#8C4A42] hover:text-[#B56258] transition-colors"
          >
            Delete entry
          </button>
          <div className="flex gap-2">
            <button
              data-testid="share-entry-btn"
              onClick={shareDay}
              className="bg-transparent border border-[#2E2A25] text-[#CFA66A] hover:bg-[#26231F] px-4 py-2 text-sm rounded-sm transition-colors font-mono uppercase tracking-widest text-xs"
            >
              Share
            </button>
            <button
              onClick={onClose}
              className="bg-transparent border border-[#2E2A25] text-[#EBE5D9] hover:bg-[#26231F] px-4 py-2 text-sm rounded-sm transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid={TID.saveEntry}
              onClick={save}
              disabled={saving}
              className="bg-[#CFA66A] text-[#0F0E0C] font-medium px-6 py-2 text-sm rounded-sm hover:bg-[#EBE5D9] transition-colors disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save entry"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
